/* eslint-disable no-bitwise */
import AddIcon from '@mui/icons-material/Add';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import ColorizeIcon from '@mui/icons-material/Colorize';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import DangerousIcon from '@mui/icons-material/Dangerous';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import NetworkWifi1BarOutlinedIcon from '@mui/icons-material/NetworkWifi1BarOutlined';
import NetworkWifi2BarOutlinedIcon from '@mui/icons-material/NetworkWifi2BarOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SignalWifiStatusbar4BarIcon from '@mui/icons-material/SignalWifiStatusbar4Bar';
import SquareRoundedIcon from '@mui/icons-material/SquareRounded';
import TheatersOutlinedIcon from '@mui/icons-material/TheatersOutlined';
import TimerIcon from '@mui/icons-material/Timer';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WifiIcon from '@mui/icons-material/Wifi';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import update from 'immutability-helper';
import _, {isArray} from 'lodash';
import {memo, useCallback, useEffect, useMemo, useRef} from 'react';

import {useConfiguration} from '../../../hooks/configuration';
import {usePrint} from '../../../hooks/print';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import {
  datagridIconSxCell as sxCell,
  datagridIconSxHeader as sxHeader,
} from '../../../hooks/theme';
import usePrevious from '../../../hooks/usePrevious';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {ExportField} from '../../../interfaces/Export';
import {
  AmsEventList,
  amsSensorAddressMask,
  GasMonitoringNode,
  GasMonitoringNodeListQuery,
  GasMonitoringNodeListResponse,
  SentroType,
} from '../../../interfaces/GasMonitoringNode';
import {MapCountByLayer} from '../../../interfaces/Map';
import reduxActions from '../../../redux/actions';
import reduxSelectors from '../../../redux/selectors';
import {
  getAmsBatteryIconColor,
  getAMSBatteryIconType,
  getAmsBatteryPercent,
  getEmoduleUnitsLabel,
} from '../../../utils/ams';
import {
  OpenedEntityMode,
  OpenedEntityType,
} from '../../../utils/connect-view-panel';
import {
  PRINT_HIDDEN_SX,
  PRINTABLE_CONTAINER_SX,
  PRINTABLE_MAP_SX,
} from '../../../utils/printStyles';
import {isPresent} from '../../../utils/type-guards';
import AMSBlastButton from '../../ams/AMSBlastButton';
import AccessControl from '../../common/AccessControl';
import {AMSBatteryIcon} from '../../common/AMSBatteryIcon';
import {AutoRefreshSettingsSelect} from '../../common/AutoRefreshSettingsSelect';
import {
  DataGridColumn,
  DataGridRef,
  DataGridSortBy,
  RenderCellParams,
} from '../../common/DataGrid';
import {Map, MapLatLangCoordinates, MapLayerId} from '../../common/Map';
import {ResizableColumns} from '../../common/ResizableColumns';
import {ZonesSelect} from '../../selectors/ZonesSelect';
import {
  AMSEModuleItemToggleCalibrationMode,
  ToggleCalibrationModeRef,
} from '../AMSEModule/AMSEModuleItemToggleCalibrationMode';
import {
  AMSEModuleItemToggleEventDelay,
  ToggleEventDelayRef,
} from '../AMSEModule/AMSEModuleItemToggleEventDelay';
import {
  AMSEModuleItemToggleInputStatusEvent,
  ToggleInputStatusEventRef,
} from '../AMSEModule/AMSEModuleItemToggleInputStatusEvent';
import AMSImportExportButton from '../AMSSensor/AMSImportExportButton';
import AMSItemsPurgeButton from '../AMSSensor/AMSItemsPurgeButton';
import AMSSensorItemAcknowledgeButton from '../AMSSensor/AMSSensorItemAcknowledgeButton';
import AMSSensorItemEditButton from '../AMSSensor/AMSSensorItemEditButton';
import {
  AMSSensorItemStandDown,
  StandDownRef,
} from '../AMSSensor/AMSSensorItemStandDown';
import {
  AMSSensorItemToggleAvaTest,
  ToggleAvaTestRef,
} from '../AMSSensor/AMSSensorItemToggleAvaTest';
import AMSSensorItemUpsertButton from '../AMSSensor/AMSSensorItemUpsertButton';
import {AMSSensorToggleNonISDevices} from '../AMSSensor/AMSSensorToggleNonISDevices';
import ReportDataGrid from './ReportDataGrid';

export interface GasMonitoringReportData {
  mapLayers: MapLayerId[];
  mapLevel: number | null;
  params: Partial<GasMonitoringNodeListQuery>;
  selectedIds: string[] | null;
  shownFields: {
    all?: string[];
  };
  grid: {
    pageSize: number;
    page: number;
  };
  exportFields: ExportField[];
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  columnFilters?: Record<string, string>;
  sortBy?: DataGridSortBy | null;
}

const DEFAULT_SHOWN_FIELDS = [
  'select',
  'edit',
  'battery_voltage',
  'disable_event',
  'alert_alarm',
  'no_heartbeat',
  'emod_conf',
  'input_state_change',
  'ava_status',
  'ventilation_status',
  'name',
  'commtrac_external_id',
  'value',
  'unit_id',
  'type_id',
  'date',
  'intrinsically_safe',
  'ava',
  'non_sensing',
  'type_2',
];

export const getGasMonitoringReportData = (): GasMonitoringReportData => ({
  mapLayers: ['mine', 'ams_sensor_nodes'],
  mapLevel: null,
  params: {
    page: 0,
    limit: 5000,
    status: 'all',
    section_ids: [],
  },
  selectedIds: [],
  shownFields: {
    all: DEFAULT_SHOWN_FIELDS,
  },
  grid: {
    page: 0,
    pageSize: 25,
  },
  exportFields: [],
  columnWidths: {},
  columnOrder: undefined,
  columnFilters: {},
  sortBy: null,
});

interface Props {
  value: GasMonitoringReportData;
  fetchedData?: GasMonitoringNodeListResponse;
  countByLayer?: MapCountByLayer[];
  isLoading?: boolean;
  locationCoordinates?: MapLatLangCoordinates;
  onOpenItem?: (
    id: number,
    entity: OpenedEntityType,
    mode?: OpenedEntityMode
  ) => void;
  onOpenHistory?: (
    id: number | string,
    type:
      | 'asset'
      | 'cn'
      | 'wifi'
      | 'wifiLongTerm'
      | 'employee'
      | 'commtracNodeByCn'
      | 'networkDiagnostics'
      | 'alarm'
      | 'alarm_log'
      | 'hazard_ai_detection_log'
      | 'hazard_ai_heatmap'
      | 'amsShortTerm'
      | 'amsLongTerm'
      | 'amsLocation'
      | 'amsEmoduleInstallationHistory'
      | 'amsEmoduleSensorHistory'
      | 'amsEmoduleCalibration'
      | 'beltHistoryReport'
      | 'beltLocationHistoryReport'
  ) => void;
  onChange?: (value?: GasMonitoringReportData) => void;
  onRefresh?: () => void;
  onChangeLocationCoordinates?: (value?: MapLatLangCoordinates) => void;
  onAcknowledgeClose: (id: number) => void;
}

const GasMonitoringReport = ({
  value,
  fetchedData,
  countByLayer,
  isLoading,
  locationCoordinates,
  onOpenItem,
  onChange,
  onRefresh = () => {},
  onChangeLocationCoordinates,
  onOpenHistory,
  onAcknowledgeClose,
}: Props) => {
  const toggleCalibrationModeRef = useRef<ToggleCalibrationModeRef | null>(
    null
  );
  const toggleEventDelayRef = useRef<ToggleEventDelayRef | null>(null);
  const toggleInputStatusEventRef = useRef<ToggleInputStatusEventRef | null>(
    null
  );
  const toggleAvaTestRef = useRef<ToggleAvaTestRef | null>(null);
  const standDownRef = useRef<StandDownRef | null>(null);
  const lastMapLevelRef = useRef<number | null>(null);

  const typeIdLabels = useAppSelector(({assets}) => assets.emoduleTypeIdLabel);
  const emoduleChannelLabel = useAppSelector(
    ({assets}) => assets.sentro8ChannelLabel
  );
  const config = useMemo(() => value ?? getGasMonitoringReportData(), [value]);

  const availableMapLayers = useMemo(
    (): MapLayerId[] => ['street', 'mine', 'alarms', 'ams_sensor_nodes'],
    []
  );

  const onSelectMapLayers = useCallback(
    (v: MapLayerId[]) => {
      onChange?.(
        update(config, {
          mapLayers: {
            $set: v,
          },
        })
      );
    },
    [onChange, config]
  );

  const onSelectLevel = useCallback(
    (v: number | null) => {
      onChange?.(
        update(config, {
          mapLevel: {
            $set: v,
          },
        })
      );
    },
    [onChange, config]
  );

  const isCompactMode = useAppSelector(reduxSelectors.app.getIsCompactMode);
  const allowBlastMode = useConfiguration('ams', 'allow_blast_mode')?.value;
  const reduxAppDispatch = useAppDispatch();
  const zones = useAppSelector(({assets}) => assets.zones);
  const layers = useAppSelector(({assets}) => assets.layers);

  /*************/
  /* data grid */
  /*************/
  const dataGridRef = useRef<DataGridRef>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);
  const [printReport] = usePrint(printContainerRef.current);
  const rows = useMemo(() => fetchedData?.items ?? [], [fetchedData]);
  const amsNodeLowBattery = useConfiguration('ams', 'ams_node_low_battery');
  const ventilationFailure = useConfiguration('ams', 'ventilation_failure');

  const eventCellWidth = useMemo(() => {
    let max = 1;
    rows.forEach((row) => {
      let count = 0;
      if (row.disable_input_event) {
        count++;
      }
      if (row?.emodules?.find((e) => e.id === row?.emodule_id)?.event_delay) {
        count++;
      }
      if (
        row?.emodules?.find((e) => e.id === row?.emodule_id)
          ?.calibration_mode_till
      ) {
        count++;
      }

      max = Math.max(max, count);
    });

    return 40 + (max - 1) * 10;
  }, [rows]);

  const renderEventsCell = useCallback(
    ({row}: RenderCellParams<GasMonitoringNode>) => {
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          {row.disable_event ? (
            <Tooltip title="Events are Disabled">
              <DoDisturbIcon />
            </Tooltip>
          ) : null}
          {row.disable_input_event ? (
            <Tooltip title="Input State Change Events are Disabled">
              <DoDisturbIcon />
            </Tooltip>
          ) : null}
          {row?.emodules?.find((e) => e.id === row?.emodule_id)?.event_delay ? (
            <Tooltip title="Event Delay is Enabled">
              <TimerIcon />
            </Tooltip>
          ) : null}
          {row?.emodules?.find((e) => e.id === row?.emodule_id)
            ?.calibration_mode_till ? (
            <Tooltip title="Calibration mode is enabled">
              <ColorizeIcon />
            </Tooltip>
          ) : null}
        </Box>
      );
    },
    []
  );

  const onToggleCalibrationMode = useCallback((pk: number, status: string) => {
    toggleCalibrationModeRef.current?.submit(pk, status);
  }, []);

  const onToggleEventDelay = useCallback((pk: number, status: string) => {
    toggleEventDelayRef.current?.submit(pk, status);
  }, []);

  const onToggleInputStatusEvent = useCallback((pk: number, status: string) => {
    toggleInputStatusEventRef.current?.submit(pk, status);
  }, []);

  const onToggleAvaTest = useCallback((pk: number, status: string) => {
    toggleAvaTestRef.current?.submit(pk, status);
  }, []);

  const onStandDown = useCallback((pk: number) => {
    standDownRef.current?.submit(pk);
  }, []);

  const shownFields = useMemo(() => {
    return config.shownFields.all;
  }, [config]);

  // Use effect here
  useEffect(() => {
    const excludedFields = [
      'actions',
      'ava',
      'device',
      'intrinsically_safe',
      'module_warming',
      'non_sensing',
      'power_source',
    ]; // ? Listed fields are not supported by BE.
    onChange?.(
      update(config, {
        exportFields: {
          $set: columns
            .filter(
              (col) => !!col.headerName && !excludedFields.includes(col.field)
            )
            .map((col) => ({
              field: col.field,
              label: col.headerName,
              hidden: !shownFields?.includes(col.field),
            })),
        },
      })
    );
  }, [shownFields]);

  const handleChangeShownFields = useCallback(
    (fields: string[]) => {
      onChange?.(
        update(config, {
          shownFields: {
            all: {$set: fields},
          },
        })
      );
    },
    [onChange, config]
  );

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = useMemo(
    () => config.selectedIds ?? [],
    [config.selectedIds]
  );

  const selectedRows = useMemo(
    () => rows.filter((i) => config.selectedIds?.includes(String(i.id))),
    [rows, config.selectedIds]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<string>();

  const toggleSelectItem = useCallback(
    (id: string, shiftKey: boolean, visibleIds: string[]) => {
      const nextSelectedIds = getNextSelectedIds({
        id,
        visibleIds,
        selectedIds: config.selectedIds ?? [],
        shiftKey,
      });
      onChange?.(
        update(config, {
          selectedIds: {
            $set: nextSelectedIds,
          },
        })
      );
    },
    [config, onChange, getNextSelectedIds]
  );

  const selectAll = useCallback(() => {
    onChange?.(
      update(config, {
        selectedIds: {
          $set: rows?.map((item) => String(item.id)) ?? [],
        },
      })
    );
  }, [onChange, config, rows]);

  const unselectAll = useCallback(() => {
    onChange?.(
      update(config, {
        selectedIds: {
          $set: [],
        },
      })
    );
  }, [onChange, config]);

  const toggleSelectAllItems = useCallback(() => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  }, [selectedItems.length, rows.length, unselectAll, selectAll]);

  useEffect(() => {
    if (
      config.selectedIds &&
      config.selectedIds.length !== selectedRows.length
    ) {
      onChange?.(
        update(config, {
          selectedIds: {
            $set: selectedRows.map((item) => String(item.id)),
          },
        })
      );
    }
  }, [config.selectedIds, selectedRows]);

  const prevSelectedAll = usePrevious(selectedAll);

  useEffect(() => {
    if (prevSelectedAll && !selectedAll) {
      selectAll();
    }
  }, [rows]);

  useEffect(() => {
    if (lastMapLevelRef.current === config.mapLevel) {
      return;
    }
    lastMapLevelRef.current = config.mapLevel;
    onChange?.(
      update(config, {
        params: {
          section_ids: {
            $set: [],
          },
          map_level: {
            $set: config.mapLevel,
          },
        },
      })
    );
  }, [config.mapLevel]);

  const mapData = useMemo(() => {
    if (fetchedData?.items && config.selectedIds?.length) {
      const commtrac_external_ids: number[] = [];
      const channelEvents: {
        [key: number]: {
          [key: number]: AmsEventList;
        };
      } = {};
      return fetchedData?.items.filter((i) => {
        if (config.selectedIds?.includes(`${i.id}`)) {
          if (!commtrac_external_ids.includes(i.commtrac_external_id)) {
            commtrac_external_ids.push(i.commtrac_external_id);
            i.channel_events = {};
            channelEvents[i.commtrac_external_id] = i.channel_events;
            if (i.emodule_id) {
              i.channel_events[i.emodule_id] = i.events;
            }
            return true;
          }

          if (i.emodule_id) {
            channelEvents[i.commtrac_external_id][i.emodule_id] = i.events;
          }
        }
      });
    }
    return [];
  }, [fetchedData, config.selectedIds, selectedItems]);

  const onOpenItemMemoized = useCallback(
    (e: number) => {
      const newSenor =
        mapData.filter((it) => it.ack === '0' && it.id === e)?.length === 1;
      onOpenItem?.(e, 'ams', newSenor ? 'ack' : 'view');
    },
    [mapData, onOpenItem]
  );

  // Refresh Interval
  const refreshPeriod = useConfiguration(
    'ams',
    'ams_grid_refresh_period'
  );

  const handleColumnWidthChange = useCallback(
    (widths: Record<string, number>) => {
      onChange?.(
        update(config, {
          columnWidths: {$set: widths},
        })
      );
    },
    [onChange, config]
  );

  const handleColumnFiltersChange = useCallback(
    (filters: Record<string, string>) => {
      onChange?.(
        update(config, {
          columnFilters: {$set: filters},
        })
      );
    },
    [onChange, config]
  );

  const handleSortChange = useCallback(
    (sortBy?: DataGridSortBy) => {
      onChange?.(
        update(config, {
          sortBy: {$set: sortBy ?? null},
        })
      );
    },
    [onChange, config]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      onChange?.(
        update(config, {
          columnOrder: {$set: order},
          shownFields: {all: {$set: fields}},
        })
      );
    },
    [onChange, config]
  );

  const selectedUnique = useMemo(() => {
    return Object.values(
      selectedRows.reduce(
        (acc, item) => {
          acc[item.commtrac_external_id] = item;
          return acc;
        },
        {} as Record<number, (typeof selectedRows)[number]>
      )
    );
  }, [selectedRows]);

  const columns: DataGridColumn<GasMonitoringNode>[] = useMemo(
    () => [
      {
        field: 'select',
        type: 'select',
        renderHeader: () => (
          <Checkbox
            color="primary"
            disabled={rows.length === 0}
            checked={selectedItems.length > 0 && selectedAll}
            indeterminate={selectedItems.length > 0 && !selectedAll}
            onChange={() => toggleSelectAllItems()}
          />
        ),
        renderCell: ({row, visibleRows}) => (
          <Checkbox
            color="primary"
            checked={selectedItems.includes(String(row.id))}
            onChange={(event) =>
              toggleSelectItem(
                String(row.id),
                isShiftClick(event),
                visibleRows.map((visibleRow) => String(visibleRow.id))
              )
            }
          />
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 40,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.id,
      },
      {
        sxHeader,
        sxCell,
        field: 'actions',
        renderHeader: () => (
          <Tooltip title="Edit">
            <MoreHorizIcon />
          </Tooltip>
        ),
        type: 'actions',
        width: 40,
        doNotExport: true,
        renderCell: ({row}) =>
          row.ack === '0' ? (
            <AccessControl permissions={['patch::/ams/:id(\\d+)']}>
              <AMSSensorItemAcknowledgeButton
                item={row}
                pk={row.id}
                locationCoordinates={locationCoordinates}
                component={IconButton}
                onSubmitted={(item) => {
                  onRefresh?.();
                  onOpenItem?.(item?.id, 'ams', 'ack');
                  reduxAppDispatch(reduxActions.assets.fetchGasMonitorNodes);
                  reduxAppDispatch(reduxActions.assets.fetchAmsEmodules);
                }}
                onClose={() => {
                  onAcknowledgeClose(row.id);
                }}
              >
                <Tooltip title="Acknowledge">
                  <WarningAmberIcon color="warning" />
                </Tooltip>
              </AMSSensorItemAcknowledgeButton>
            </AccessControl>
          ) : (
            <AMSSensorItemEditButton
              item={row}
              onOpenItem={onOpenItem}
              onOpenHistory={onOpenHistory}
              onToggleCalibrationMode={onToggleCalibrationMode}
              onToggleEventDelay={onToggleEventDelay}
              onToggleInputStatusEvent={onToggleInputStatusEvent}
              onToggleAvaTest={onToggleAvaTest}
              onStandDown={onStandDown}
              onSubmitted={() => onRefresh?.()}
              component={IconButton}
              componentProps={{
                color: 'primary',
              }}
            >
              <MoreHorizIcon />
            </AMSSensorItemEditButton>
          ),
      },
      {
        field: 'battery_voltage',
        headerName: 'Low Battery',
        width: 40,
        valueGetter: ({row}) => {
          // Constants
          const externalVoltage = row?.external_voltage;
          const batteryVoltage = row?.battery_voltage;
          const isOnExternalCharger = row?.is_on_external_charger;
          const sentroType = row?.sentro_type;

          // Return highest values in case no required data
          if (
            !externalVoltage &&
            !batteryVoltage &&
            sentroType !== SentroType.SENTRO_8
          ) {
            return 10;
          }

          // Get a value of used color as a number to be able to sort final result
          const iconColor = getAmsBatteryIconColor({
            batteryVoltage,
            externalVoltage,
            isOnExternalCharger,
            amsNodeLowBatteryValue: amsNodeLowBattery?.value,
            isLowBatteryEvent: row?.events.e_405,
            isLowExternalChargeEvent: row?.events.e_409,
            sentroType,
          });
          const iconColorNumberValue = iconColor ? 3 : 2;

          // Get a value of used icon type as a number
          const iconType = getAMSBatteryIconType({
            batteryVoltage,
            externalVoltage,
            isOnExternalCharger,
            sentroType,
          });
          const iconTypeNumberValues: Record<typeof iconType, number> = {
            power: 1,
            batteryEmpty: 2,
            batteryLow: 3,
            batteryMid: 4,
            batteryHigh: 5,
            batteryFull: 6,
          };
          const currentIconTypeNumberValue = iconTypeNumberValues[iconType];

          return iconColorNumberValue + currentIconTypeNumberValue;
        },
        renderHeader: () => (
          <Tooltip title="Low Battery">
            <BatteryChargingFullIcon />
          </Tooltip>
        ),
        sortable: true,
        renderCell: ({row}) => {
          const externalVoltage = row?.external_voltage;
          const batteryVoltage = row?.battery_voltage;
          const isOnExternalCharger = row?.is_on_external_charger;
          const sentroType = row?.sentro_type;
          const isSentro8 = sentroType === SentroType.SENTRO_8;

          if (!externalVoltage && !batteryVoltage && !isSentro8) {
            return (
              <Tooltip title="Unknown">
                <Box
                  display="flex"
                  justifyContent="left"
                  sx={{paddingLeft: 0.5}}
                >
                  <p>?</p>
                </Box>
              </Tooltip>
            );
          }

          const tooltipContent = isSentro8
            ? 'External Power'
            : !isOnExternalCharger || !externalVoltage || externalVoltage < 2
              ? getAmsBatteryPercent(batteryVoltage)
              : `${externalVoltage} v`;
          const iconColor = getAmsBatteryIconColor({
            batteryVoltage,
            externalVoltage,
            isOnExternalCharger,
            amsNodeLowBatteryValue: amsNodeLowBattery?.value,
            isLowBatteryEvent: row?.events.e_405,
            isLowExternalChargeEvent: row?.events.e_409,
            sentroType,
          });
          const iconType = getAMSBatteryIconType({
            batteryVoltage,
            externalVoltage,
            isOnExternalCharger,
            sentroType,
          });
          const iconColorProp: {color?: 'error'} = iconColor
            ? {color: iconColor}
            : {};

          return (
            <Tooltip title={tooltipContent}>
              {iconType === 'power' ? (
                <PowerSettingsNewOutlinedIcon {...iconColorProp} />
              ) : (
                <div>
                  <AMSBatteryIcon iconType={iconType} {...iconColorProp} />
                </div>
              )}
            </Tooltip>
          );
        },
      },
      {
        field: 'disable_event',
        headerName: 'Events',
        width: 40,
        resizeable: true,
        sxHeader: {minWidth: eventCellWidth},
        valueGetter: ({row}) => row.disable_event,
        renderHeader: () => (
          <Tooltip title="Events">
            <DoDisturbIcon />
          </Tooltip>
        ),
        sortable: true,
        renderCell: renderEventsCell,
      },
      {
        field: 'alert_alarm',
        headerName: 'Alert/Alarm',
        width: 40,
        valueGetter: ({row}) => {
          let value = 0;

          if (
            row.events.e_402 ||
            row.events.e_452 ||
            row.events.e_462 ||
            row.events.e_474 ||
            row.events.e_484
          ) {
            value += 2;
          }

          if (
            row.events.e_403 ||
            row.events.e_453 ||
            row.events.e_463 ||
            row.events.e_475 ||
            row.events.e_483
          ) {
            value++;
          }

          return value;
        },
        renderHeader: () => (
          <Tooltip title="Alert/Alarm">
            <WarningIcon />
          </Tooltip>
        ),
        sortable: true,
        renderCell: ({row}) => {
          // Values accumulated since several events can occur at same time
          if (
            (row.events.e_402 ||
              row.events.e_452 ||
              row.events.e_462 ||
              row.events.e_474 ||
              row.events.e_484) &&
            row.disable_event === 0
          ) {
            return (
              <Tooltip title="Alarm">
                <WarningIcon color="error" />
              </Tooltip>
            );
          }

          if (
            (row.events.e_403 ||
              row.events.e_453 ||
              row.events.e_463 ||
              row.events.e_475 ||
              row.events.e_483) &&
            row.disable_event === 0
          ) {
            return (
              <Tooltip title="Alert">
                <WarningIcon color="warning" />
              </Tooltip>
            );
          }

          return null;
        },
      },
      {
        field: 'no_heartbeat',
        headerName: 'Heartbeat',
        width: 40,
        valueGetter: ({row}) => {
          // Values accumulated since several events can occur at same time
          let value = 0;

          if (row.events.e_407) {
            value += 4;
          }

          if (row.events.e_404) {
            value += 2;
          }

          if (row.events.e_406) {
            value++;
          }

          return value;
        },
        renderHeader: () => (
          <Tooltip title="Heartbeat">
            <WifiIcon />
          </Tooltip>
        ),
        sortable: true,
        renderCell: ({row}) => {
          // ATEST values are not taken into account in current 5.3 version
          if (row.events.e_407) {
            return (
              <Tooltip title="AMS Sensors many ENV messages">
                <SignalWifiStatusbar4BarIcon color="error" />
              </Tooltip>
            );
          }

          if (row.events.e_404) {
            return (
              <Tooltip title="AMS Sensors no ENV messages">
                <NetworkWifi1BarOutlinedIcon color="error" />
              </Tooltip>
            );
          }

          if (row.events.e_406) {
            return (
              <Tooltip title="AMS Sensors few ENV messages">
                <NetworkWifi2BarOutlinedIcon color="warning" />
              </Tooltip>
            );
          }

          return '';
        },
      },
      {
        field: 'emod_conf',
        headerName: 'EMod Conf',
        width: 40,
        renderHeader: () => (
          <Tooltip title="EMod Conf">
            <TimerOutlinedIcon />
          </Tooltip>
        ),
        valueGetter: ({row}) => {
          if (row.events.e_408 && row.events.e_410) {
            return 3;
          }

          if (row.events.e_410) {
            return 2;
          }

          if (row.events.e_408) {
            return 1;
          }
        },
        sortable: true,
        renderCell: ({row}) => {
          if (row.events.e_410) {
            return (
              <Tooltip title="e-Module Unregistered">
                <TheatersOutlinedIcon />
              </Tooltip>
            );
          }

          if (row.events.e_408) {
            return (
              <Tooltip title="e-Module Calibration Date Expired">
                <TimerOutlinedIcon color="error" />
              </Tooltip>
            );
          }

          return null;
        },
      },
      {
        field: 'input_state_change',
        headerName: 'Input State Change',
        width: 40,
        valueGetter: ({row}) => {
          return row.events.e_430 ? 1 : 0;
        },
        renderHeader: () => (
          <Tooltip title="Input State Change">
            <ChangeCircleIcon />
          </Tooltip>
        ),
        sortable: true,
        renderCell: ({row}) => {
          if (row.events.e_430) {
            return (
              <Tooltip title="Input State Change">
                <ChangeCircleIcon color="primary" />
              </Tooltip>
            );
          }

          return null;
        },
      },
      {
        field: 'ava_status',
        headerName: 'AVA Status',
        width: 40,
        valueGetter: ({row}) => {
          if (row.ava) {
            const avaStatusAck = row?.ava_status_ack;
            const avaStatus = row?.ava_status;

            const avaIconNumberValue = avaStatus === avaStatusAck ? 20 : 10;

            if (avaStatus === 402) {
              return avaIconNumberValue + 3;
            }

            if (avaStatus === 403) {
              return avaIconNumberValue + 2;
            }

            return avaIconNumberValue + 1;
          }

          return 0;
        },
        renderHeader: () => (
          <Tooltip title="AVA Status">
            <FiberManualRecordIcon />
          </Tooltip>
        ),
        sortable: true,
        renderCell: ({row}) => {
          if (row.ava) {
            const avaStatusAck = row?.ava_status_ack;
            const avaStatus = row?.ava_status;

            const AvaIcon =
              avaStatus === avaStatusAck
                ? FiberManualRecordIcon
                : FiberManualRecordOutlinedIcon;

            if (avaStatus === 402) {
              return (
                <Tooltip title="Alarm">
                  <AvaIcon color="error" />
                </Tooltip>
              );
            }

            if (avaStatus === 403) {
              return (
                <Tooltip title="Alert">
                  <AvaIcon color="warning" />
                </Tooltip>
              );
            }

            if (avaStatus === 440 && avaStatusAck === 440) {
              return (
                <Tooltip title="AVA test message acked">
                  <WbSunnyIcon color="success" />
                </Tooltip>
              );
            } else if (avaStatus === 440) {
              return (
                <Tooltip title="AVA test message sent">
                  <WbSunnyIcon color="disabled" />
                </Tooltip>
              );
            }

            return (
              <Tooltip title="Ok">
                <AvaIcon color="success" />
              </Tooltip>
            );
          }

          return '';
        },
      },
      {
        field: 'ventilation_status',
        headerName: 'Ventilation Status',
        width: 40,
        valueGetter: ({row}) => {
          if (
            ventilationFailure?.value &&
            !row?.intrinsically_safe &&
            row?.ventilation_status
          ) {
            return row?.ventilation_status_ack ? 2 : 1;
          }

          return 0;
        },
        renderHeader: () => (
          <Tooltip title="Ventilation Status">
            <CropSquareIcon />
          </Tooltip>
        ),
        sortable: true,
        renderCell: ({row}) => {
          if (
            ventilationFailure?.value &&
            !row?.intrinsically_safe &&
            row?.ventilation_status
          ) {
            return row?.ventilation_status_ack ? (
              <Tooltip title="ACK">
                <SquareRoundedIcon color="error" />
              </Tooltip>
            ) : (
              <Tooltip title="SENT">
                <CropSquareIcon color="error" />
              </Tooltip>
            );
          }

          return '';
        },
      },
      {
        sxCell,
        field: 'name',
        headerName: 'Name',
        width: 160,
        resizeable: true,
        sortable: true,
        filterable: true,
        filterPlaceholder: 'Filter by name...',
        valueGetter: ({row}) => row.name,
      },
      {
        field: 'commtrac_external_id',
        headerName: 'Network ID',
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) =>
          row?.commtrac_external_id & amsSensorAddressMask,
      },
      {
        field: 'value',
        headerName: 'Value',
        sxCell: {textAlign: 'center'},
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.value,
      },
      {
        field: 'unit_id',
        headerName: 'Units',
        width: 60,
        resizeable: true,
        sxCell: {textAlign: 'center'},
        sortable: true,
        valueGetter: ({row}) =>
          getEmoduleUnitsLabel(row?.type_id, row?.unit_id),
      },
      {
        field: 'type_id',
        headerName: 'Type',
        sxCell: {textAlign: 'left'},
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => {
          if (_.isNull(row.type_id)) {
            return 'None';
          } else {
            return typeIdLabels?.[row.type_id];
          }
        },
      },
      {
        field: 'zone_id',
        headerName: 'Section',
        sxCell: {textAlign: 'left'},
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => zones.find((i) => i.id === row.zone_id)?.name,
      },
      {
        field: 'layer_id',
        headerName: 'Layer',
        width: 80,
        sortable: true,
        resizeable: true,
        valueGetter: ({row}) => layers.find((i) => i.id === row.layer_id)?.name,
      },
      {
        field: 'date',
        headerName: 'Date',
        width: 160,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.date,
      },
      {
        field: 'intrinsically_safe',
        headerName: 'IS',
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => (row.intrinsically_safe === 1 ? 'Yes' : 'No'),
      },
      {
        field: 'ava',
        headerName: 'AVA',
        sxCell: {textAlign: 'center'},
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.ava,
        renderCell: ({row}) => {
          if (row?.ava === 0) {
            return 'No';
          }

          if (row?.ava === 1) {
            return 'Yes';
          }

          return '';
        },
      },
      {
        field: 'non_sensing',
        headerName: 'Non-Sensing',
        sxCell: {textAlign: 'center'},
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.non_sensing,
        renderCell: ({row}) => {
          if (row?.non_sensing === 0) {
            return 'No';
          }

          if (row?.non_sensing === 1) {
            return 'Yes';
          }

          return '';
        },
      },
      {
        field: 'type_2',
        headerName: 'Type 2',
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.type_2,
      },
      {
        field: 'sensor_serial_number',
        headerName: 'Sensor SN',
        sxCell: {textAlign: 'center'},
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.sensor_serial_number ?? '',
      },
      {
        field: 'external_voltage',
        headerName: 'Ext Voltage',
        sxCell: {textAlign: 'center'},
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.external_voltage ?? '',
      },
      {
        field: 'pos_lat',
        headerName: 'Lat',
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.pos_lat ?? '',
      },
      {
        field: 'pos_lon',
        headerName: 'Lon',
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.pos_lon ?? '',
      },
      {
        field: 'power_source',
        headerName: 'Power Source',
        sxCell: {textAlign: 'center'},
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.power_source ?? '',
      },
      {
        field: 'callibration_date',
        headerName: 'Calibaration Date',
        width: 180,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.callibration_date ?? '',
      },
      {
        field: 'module_absent',
        headerName: 'Module Absent',
        sxCell: {textAlign: 'center'},
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.module_absent ?? '',
      },
      {
        field: 'module_warming',
        headerName: 'Module Warning',
        sxCell: {textAlign: 'center'},
        width: 130,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.module_warming ?? '',
      },
      {
        field: 'point1_tripped',
        headerName: 'Point1 Tripped',
        sxCell: {textAlign: 'center'},
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.point1_tripped ?? '',
      },
      {
        field: 'point2_tripped',
        headerName: 'Point2 Tripped',
        sxCell: {textAlign: 'center'},
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.point2_tripped ?? '',
      },
      {
        field: 'stel_alarm',
        headerName: 'Stel Alarm',
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.stel_alarm ?? '',
      },
      {
        field: 'twa_alarm',
        headerName: 'Twa Alarm',
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.twa_alarm ?? '',
      },
      {
        field: 'fault',
        headerName: 'Fault',
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.fault ?? '',
      },
      {
        field: 'pellister_over',
        headerName: 'Pellister Over',
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.pellister_over ?? '',
      },
      {
        field: 'input1',
        headerName: 'Input1',
        width: 70,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.input1 ?? '',
      },
      {
        field: 'input2',
        headerName: 'Input2',
        width: 70,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.input2 ?? '',
      },
      {
        field: 'output1',
        headerName: 'Output1',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.output1 ?? '',
      },
      {
        field: 'output2',
        headerName: 'Output2',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row?.output2 ?? '',
      },
      {
        field: 'channel',
        headerName: 'Channel',
        width: 80,
        resizeable: true,
        sortable: true,
        renderCell: ({row}) => {
          return emoduleChannelLabel && row.channel
            ? emoduleChannelLabel[row.channel]
            : '';
        },
      },
    ],
    [
      rows,
      selectedItems,
      selectedAll,
      toggleSelectAllItems,
      toggleSelectItem,
      eventCellWidth,
      renderEventsCell,
      amsNodeLowBattery,
      onRefresh,
      onOpenItem,
      reduxAppDispatch,
      onAcknowledgeClose,
      onOpenHistory,
      onToggleCalibrationMode,
      onToggleAvaTest,
      onStandDown,
      locationCoordinates,
      typeIdLabels,
      zones,
      layers,
      emoduleChannelLabel,
      ventilationFailure,
    ]
  );

  const sxFooter = useMemo(
    () => ({
      bgcolor: (theme: any) =>
        theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
    }),
    []
  );

  const footerStart = useMemo(
    () =>
      selectedItems.length ? (
        <Box display="flex" alignItems="center" gap={3}>
          <Box
            display="flex"
            gap={0.5}
            alignItems="center"
            height="100%"
            whiteSpace="nowrap"
          >
            {selectedUnique.length} selected
          </Box>
          <AccessControl
            permissions={['post::/purge']}
            amountAffected={
              new Set(selectedRows.map((item) => item.commtrac_external_id))
                .size
            }
          >
            <Box display="flex" height={40}>
              <AMSItemsPurgeButton
                amsIds={selectedRows
                  .filter((i) => i.id)
                  .map((i) => i.id)
                  .filter(isPresent)}
                componentProps={{size: 'small', variant: 'outlined'}}
                onDone={() => onRefresh?.()}
              >
                <RemoveCircleOutlineIcon fontSize="small" sx={{mr: 1}} />
                Purge
              </AMSItemsPurgeButton>
            </Box>
          </AccessControl>
        </Box>
      ) : null,
    [selectedItems, selectedRows, onRefresh, selectedUnique]
  );

  const handlePageChange = useCallback(
    (v: number) => {
      onChange?.(update(config, {grid: {page: {$set: v}}}));
    },
    [onChange, config]
  );

  const handlePageSizeChange = useCallback(
    (v: number) => {
      onChange?.(update(config, {grid: {pageSize: {$set: v}}}));
    },
    [onChange, config]
  );

  return (
    <Box height="100%" width="100%">
      <Box ref={printContainerRef} sx={PRINTABLE_CONTAINER_SX}>
        <ResizableColumns
          left={
            <>
              <Box
                display="flex"
                alignItems="center"
                height={isCompactMode ? 56 : 70}
                p={isCompactMode ? 1 : 2}
                minWidth={520}
                gap={2}
                sx={PRINT_HIDDEN_SX}
              >
                <AMSSensorToggleNonISDevices
                  onDone={() => onRefresh?.()}
                  refreshPeriod={refreshPeriod}
                />

                {allowBlastMode === '1' && (
                  <AccessControl permissions={['patch::/ams/blast_mode']}>
                    <AMSBlastButton
                      componentProps={{
                        color: 'primary',
                        size: 'small',
                        startIcon: <DangerousIcon />,
                      }}
                    />
                  </AccessControl>
                )}
              </Box>
              <Box height="100%">
                <Map
                  panel="ams_sensor_nodes_report"
                  amsSensorNodes={mapData}
                  countByLayer={countByLayer}
                  selectedMapLayers={config.mapLayers}
                  selectedLevel={config.mapLevel}
                  height="calc(100% - 70px)"
                  minWidth={200}
                  availableMapLayers={availableMapLayers}
                  onSelectMapLayers={onSelectMapLayers}
                  onSelectLevel={onSelectLevel}
                  onOpenItem={onOpenItemMemoized}
                  onOpenHistory={onOpenHistory}
                  onGetClickCoordinates={onChangeLocationCoordinates}
                  sx={PRINTABLE_MAP_SX}
                />
              </Box>
            </>
          }
        >
          <Box display="flex" flexDirection="column" height="100%">
            <Box
              py={isCompactMode ? 1 : 2}
              pr={isCompactMode ? 1 : 2}
              minWidth={800}
              sx={PRINT_HIDDEN_SX}
            >
              <Box
                display="flex"
                flexDirection="row-reverse"
                justifyContent="space-between"
                alignItems="center"
                height={40}
                gap={2}
              >
                <Box display="flex" height="100%">
                  <ButtonGroup>
                    <AccessControl permissions={['post::/ams']}>
                      <AMSSensorItemUpsertButton
                        onSubmitted={() => {
                          onRefresh?.();
                          onChangeLocationCoordinates?.(undefined);
                        }}
                        locationCoordinates={locationCoordinates}
                        componentProps={{
                          color: 'primary',
                          size: 'small',
                          startIcon: <AddIcon />,
                        }}
                      >
                        <Tooltip title="Add Sensor">
                          <span>Sensor</span>
                        </Tooltip>
                      </AMSSensorItemUpsertButton>
                    </AccessControl>

                    <AMSImportExportButton
                      value={value}
                      component={Button}
                      componentProps={{color: 'primary'}}
                      onSubmitted={() => onRefresh?.()}
                    >
                      <ImportExportIcon />
                    </AMSImportExportButton>

                    <Button onClick={() => onRefresh?.()}>
                      <Tooltip title="Refresh">
                        <RefreshIcon />
                      </Tooltip>
                    </Button>

                    {refreshPeriod ? (
                      <AutoRefreshSettingsSelect
                        refreshPeriod={refreshPeriod}
                      />
                    ) : null}
                    <Button onClick={() => printReport()}>
                      <Tooltip title="Print">
                        <PrintIcon />
                      </Tooltip>
                    </Button>
                  </ButtonGroup>
                </Box>
                <Box flexGrow={2} display="flex" height="100%">
                  <ZonesSelect
                    value={config.params.section_ids}
                    multiple={true}
                    layer={config.mapLevel}
                    size="small"
                    nullLabel="All Sections"
                    fullWidth
                    maxDefaultWidth={367}
                    onChange={(v) => {
                      const sectionIds = isArray(v) ? v : [+v];
                      onChange?.(
                        update(config, {
                          params: {
                            section_ids: {
                              $set: sectionIds as number[],
                            },
                          },
                        })
                      );
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <ReportDataGrid
              refObj={dataGridRef}
              rows={rows}
              columns={columns as any}
              page={config.grid.page}
              pageSize={config.grid.pageSize}
              loading={isLoading}
              shownFields={shownFields}
              defaultShownFields={DEFAULT_SHOWN_FIELDS}
              columnWidths={config.columnWidths}
              columnOrder={config.columnOrder}
              columnFilters={config.columnFilters ?? {}}
              sortBy={config.sortBy ?? null}
              sxFooter={sxFooter}
              footerStart={footerStart}
              onSort={handleSortChange}
              onShownFieldsChange={handleChangeShownFields}
              onColumnWidthsChange={handleColumnWidthChange}
              onColumnOrderAndShownFieldsChange={
                handleColumnOrderAndShownFieldsChange
              }
              onColumnFiltersChange={handleColumnFiltersChange}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </Box>
        </ResizableColumns>
        <AMSEModuleItemToggleCalibrationMode
          ref={toggleCalibrationModeRef}
          onSubmitted={() => onRefresh?.()}
        />
        <AMSEModuleItemToggleEventDelay
          ref={toggleEventDelayRef}
          onSubmitted={() => onRefresh?.()}
        />
        <AMSEModuleItemToggleInputStatusEvent
          ref={toggleInputStatusEventRef}
          onSubmitted={() => onRefresh?.()}
        />
        <AMSSensorItemToggleAvaTest
          ref={toggleAvaTestRef}
          onSubmitted={() => onRefresh?.()}
        />
        <AMSSensorItemStandDown
          ref={standDownRef}
          onSubmitted={() => onRefresh?.()}
        />
      </Box>
    </Box>
  );
};

export default memo(GasMonitoringReport);
